/**
 * Visual story map for the play.
 *
 * Reads `scenes` + `lineCatalog` from script.ts and renders a directed graph:
 *   - Each scene becomes a node (HTML card).
 *   - Linear `goto` beats become unlabeled edges.
 *   - `choice` beats fan out into one labeled edge per option.
 *   - `end` beats land in a synthetic ending leaf node.
 *
 * The page rebuilds from scratch on every load, so any script change is
 * reflected with a refresh — no manual sync.
 */

import {
  stories,
  lineCatalog,
  type Beat,
  type Scene,
  type Story,
} from './script.ts';
import dagre from 'dagre';
import { attachCustomCursor } from './cursor.ts';

attachCustomCursor();

// ───── graph types ───────────────────────────────────────────────────────

type EdgeKind = 'goto' | 'choice' | 'end';

type GraphEdge = {
  from: string;
  to: string;
  kind: EdgeKind;
  label?: string;
};

type GraphNode = {
  id: string;
  scene?: Scene;
  ending?: string;
  isEnding?: boolean;
};

// ───── 1. build graph from scenes ────────────────────────────────────────

function buildGraph(story: Story): { nodes: Map<string, GraphNode>; edges: GraphEdge[] } {
  const nodes = new Map<string, GraphNode>();
  const edges: GraphEdge[] = [];

  for (const [id, scene] of Object.entries(story.scenes)) {
    nodes.set(id, { id, scene });
  }

  for (const [id, scene] of Object.entries(story.scenes)) {
    let branchFound = false;
    for (const beat of scene.beats) {
      if (beat.type === 'choice') {
        branchFound = true;
        for (const opt of beat.options) {
          edges.push({ from: id, to: opt.goto, kind: 'choice', label: opt.text });
        }
      } else if (beat.type === 'voice_prompt') {
        branchFound = true;
        edges.push({ from: id, to: beat.onSpeak,   kind: 'choice', label: '🎙 spoke' });
        edges.push({ from: id, to: beat.onSilence, kind: 'choice', label: '— silent' });
      } else if (beat.type === 'branch_if_spoke') {
        branchFound = true;
        edges.push({ from: id, to: beat.whenSpoken, kind: 'choice', label: 'has spoken' });
        edges.push({ from: id, to: beat.whenSilent, kind: 'choice', label: 'silent' });
      } else if (beat.type === 'branch_if_flag') {
        branchFound = true;
        const eq = beat.equals !== undefined ? `=${beat.equals}` : '';
        edges.push({ from: id, to: beat.whenMatch, kind: 'choice', label: `if ${beat.key}${eq}` });
        edges.push({ from: id, to: beat.whenNotMatch, kind: 'choice', label: `else` });
      } else if (beat.type === 'branch_if_count') {
        branchFound = true;
        edges.push({ from: id, to: beat.whenAtLeast, kind: 'choice', label: `≥${beat.atLeast} of ${beat.flags.length}` });
        edges.push({ from: id, to: beat.whenBelow, kind: 'choice', label: `<${beat.atLeast}` });
      } else if (beat.type === 'conversation' && !branchFound) {
        edges.push({ from: id, to: beat.onExit, kind: 'goto', label: '🗣 chat' });
      } else if (beat.type === 'goto' && !branchFound) {
        edges.push({ from: id, to: beat.scene, kind: 'goto' });
      } else if (beat.type === 'end') {
        const endingId = `_end_${beat.ending}`;
        if (!nodes.has(endingId)) {
          nodes.set(endingId, {
            id: endingId,
            ending: beat.ending,
            isEnding: true,
          });
        }
        edges.push({ from: id, to: endingId, kind: 'end' });
      }
    }
  }

  return { nodes, edges };
}

// ───── 2. render with dagre ──────────────────────────────────────────────

const NODE_W = 260;
const NODE_H = 160;
const RANK_SEP = 90;   // vertical gap between rows
const NODE_SEP = 36;   // horizontal gap between sibling nodes within a row
const PAD = 60;

const svgNS = 'http://www.w3.org/2000/svg';

/** dagre routes edges as a polyline through {x,y} bend points. We turn that
 *  into a smooth path: a Catmull-Rom-ish chain of cubic Béziers between
 *  consecutive points. Looks much cleaner than zigzag straight segments. */
function smoothPath(pts: Array<{ x: number; y: number }>): string {
  if (pts.length < 2) return '';
  if (pts.length === 2) {
    const [a, b] = pts;
    return `M ${a.x} ${a.y} L ${b.x} ${b.y}`;
  }
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] ?? p2;
    const t = 0.18; // tension — lower = tighter to original points
    const c1x = p1.x + (p2.x - p0.x) * t;
    const c1y = p1.y + (p2.y - p0.y) * t;
    const c2x = p2.x - (p3.x - p1.x) * t;
    const c2y = p2.y - (p3.y - p1.y) * t;
    d += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2.x} ${p2.y}`;
  }
  return d;
}

function render(story: Story) {
  const { nodes, edges } = buildGraph(story);

  // Run dagre — proper Sugiyama-style layout with crossing minimization.
  const g = new dagre.graphlib.Graph({ multigraph: true });
  g.setGraph({
    rankdir: 'TB',
    nodesep: NODE_SEP,
    ranksep: RANK_SEP,
    marginx: PAD,
    marginy: PAD,
    ranker: 'tight-tree',
  });
  g.setDefaultEdgeLabel(() => ({}));
  for (const id of nodes.keys()) {
    g.setNode(id, { width: NODE_W, height: NODE_H });
  }
  edges.forEach((e, i) => {
    g.setEdge(e.from, e.to, { kind: e.kind }, `${i}`);
  });
  dagre.layout(g);

  const graphMeta = g.graph();
  const totalW = graphMeta.width  ?? 800;
  const totalH = graphMeta.height ?? 600;

  const container = document.getElementById('graph')!;
  container.innerHTML = '';
  container.style.width = `${totalW}px`;
  container.style.height = `${totalH}px`;

  // SVG layer beneath the cards for the routed connector paths.
  const svg = document.createElementNS(svgNS, 'svg');
  svg.setAttribute('width', `${totalW}`);
  svg.setAttribute('height', `${totalH}`);
  svg.classList.add('story-edges');

  // Arrowhead markers — one per edge style so the color matches.
  const defs = document.createElementNS(svgNS, 'defs');
  for (const kind of ['goto', 'choice', 'end'] as const) {
    const m = document.createElementNS(svgNS, 'marker');
    m.setAttribute('id', `arrow-${kind}`);
    m.setAttribute('viewBox', '0 0 10 10');
    m.setAttribute('refX', '9');
    m.setAttribute('refY', '5');
    m.setAttribute('markerWidth', '6');
    m.setAttribute('markerHeight', '6');
    m.setAttribute('orient', 'auto-start-reverse');
    const tip = document.createElementNS(svgNS, 'path');
    tip.setAttribute('d', 'M 0 0 L 10 5 L 0 10 z');
    tip.setAttribute('class', `arrow-tip arrow-tip-${kind}`);
    m.appendChild(tip);
    defs.appendChild(m);
  }
  svg.appendChild(defs);
  container.appendChild(svg);

  // Render edges along dagre's bend points with smoothed paths + arrowheads.
  g.edges().forEach((edgeRef) => {
    const edgeData = g.edge(edgeRef);
    const meta = edges[Number(edgeRef.name)];
    if (!meta) return;
    const points = (edgeData.points ?? []) as Array<{ x: number; y: number }>;
    if (points.length < 2) return;

    const path = document.createElementNS(svgNS, 'path');
    path.setAttribute('d', smoothPath(points));
    path.setAttribute('class', `story-edge story-edge-${meta.kind}`);
    path.setAttribute('marker-end', `url(#arrow-${meta.kind})`);
    svg.appendChild(path);

    if (meta.label) {
      // Label sits at the midpoint of the routed polyline.
      const mid = points[Math.floor(points.length / 2)];
      const text = document.createElementNS(svgNS, 'text');
      text.setAttribute('x', `${mid.x}`);
      text.setAttribute('y', `${mid.y}`);
      text.setAttribute('class', 'story-edge-label');
      text.textContent = meta.label;
      svg.appendChild(text);
      requestAnimationFrame(() => {
        const bbox = text.getBBox();
        const bg = document.createElementNS(svgNS, 'rect');
        bg.setAttribute('x', `${bbox.x - 8}`);
        bg.setAttribute('y', `${bbox.y - 4}`);
        bg.setAttribute('width', `${bbox.width + 16}`);
        bg.setAttribute('height', `${bbox.height + 8}`);
        bg.setAttribute('rx', '4');
        bg.setAttribute('class', `story-edge-label-bg story-edge-label-bg-${meta.kind}`);
        svg.insertBefore(bg, text);
      });
    }
  });

  // Render nodes — dagre gives center coordinates; we offset to top-left.
  for (const id of nodes.keys()) {
    const node = nodes.get(id)!;
    const layout = g.node(id);
    if (!layout) continue;
    const card = document.createElement('div');
    card.className = node.isEnding ? 'story-node story-node-ending' : 'story-node';
    card.dataset.scene = id;
    card.style.left = `${layout.x - NODE_W / 2}px`;
    card.style.top = `${layout.y - NODE_H / 2}px`;
    card.style.width = `${NODE_W}px`;
    card.style.height = `${NODE_H}px`;
    card.innerHTML = renderNodeContent(node);
    container.appendChild(card);
  }

  // Recenter the viewport on the graph after a layout change.
  resetViewport(totalW, totalH);
}

// ───── pan & zoom ────────────────────────────────────────────────────────

let panX = 0, panY = 0, scale = 1;

function applyTransform() {
  const container = document.getElementById('graph')!;
  container.style.transform = `translate(${panX}px, ${panY}px) scale(${scale})`;
}

function resetViewport(graphW: number, graphH: number) {
  const main = document.querySelector('.story-main') as HTMLElement | null;
  if (!main) return;
  const fit = Math.min(
    (main.clientWidth - 80) / graphW,
    (main.clientHeight - 80) / graphH,
    1, // never zoom in past 100% on initial fit
  );
  scale = Math.max(0.25, fit);
  panX = (main.clientWidth - graphW * scale) / 2;
  panY = 20;
  applyTransform();
}

function setupPanZoom() {
  const main = document.querySelector('.story-main') as HTMLElement | null;
  if (!main || (main as any)._panZoomBound) return;
  (main as any)._panZoomBound = true;

  let dragging = false;
  let dragStartX = 0, dragStartY = 0, dragOriginX = 0, dragOriginY = 0;
  main.addEventListener('mousedown', (e) => {
    if ((e.target as HTMLElement).closest('.story-node-details')) return;
    dragging = true;
    dragStartX = e.clientX;
    dragStartY = e.clientY;
    dragOriginX = panX;
    dragOriginY = panY;
    main.classList.add('dragging');
  });
  window.addEventListener('mousemove', (e) => {
    if (!dragging) return;
    panX = dragOriginX + (e.clientX - dragStartX);
    panY = dragOriginY + (e.clientY - dragStartY);
    applyTransform();
  });
  window.addEventListener('mouseup', () => {
    dragging = false;
    main.classList.remove('dragging');
  });
  main.addEventListener('wheel', (e) => {
    e.preventDefault();
    const rect = main.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const factor = e.deltaY > 0 ? 0.9 : 1.1;
    const newScale = Math.max(0.2, Math.min(2.5, scale * factor));
    // zoom toward the cursor
    panX = mx - (mx - panX) * (newScale / scale);
    panY = my - (my - panY) * (newScale / scale);
    scale = newScale;
    applyTransform();
  }, { passive: false });
}

function renderNodeContent(node: GraphNode): string {
  if (node.isEnding) {
    return `
      <div class="story-node-tag">ending</div>
      <h3 class="story-node-title">${escape(node.ending ?? '')}</h3>
      <p class="story-node-meta">— curtain —</p>
    `;
  }
  const scene = node.scene!;
  const lines = scene.beats.filter((b): b is Extract<Beat, { type: 'line' }> => b.type === 'line');
  const voices = new Set<string>();
  for (const l of lines) {
    const entry = lineCatalog[l.key];
    if (entry) voices.add(entry.voice);
  }
  const beatRows = scene.beats.map(b => `<li>${formatBeat(b)}</li>`).join('');
  return `
    <div class="story-node-tag">${tagForScene(node.id)}</div>
    <h3 class="story-node-title">${escape(node.id)}</h3>
    <p class="story-node-meta">
      ${lines.length} line${lines.length === 1 ? '' : 's'}
      ${voices.size ? '· ' + [...voices].map(escape).join(', ') : ''}
    </p>
    <details class="story-node-details">
      <summary>${scene.beats.length} beats</summary>
      <ol class="story-beat-list">${beatRows}</ol>
    </details>
  `;
}

let currentStory: Story = Object.values(stories)[0];

function tagForScene(id: string): string {
  if (id === currentStory.firstScene) return 'start';
  if (id.startsWith('act1')) return 'act 1';
  if (id.startsWith('act2')) return 'act 2';
  if (id.startsWith('ending') || id.startsWith('end_')) return 'finale';
  return 'scene';
}

function buildTabs() {
  const tabsEl = document.getElementById('story-tabs')!;
  tabsEl.innerHTML = '';
  for (const [id, story] of Object.entries(stories)) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'story-tab';
    btn.dataset.story = id;
    btn.textContent = story.title;
    if (story === currentStory) btn.classList.add('active');
    btn.addEventListener('click', () => {
      currentStory = story;
      tabsEl.querySelectorAll('.story-tab').forEach(t => t.classList.remove('active'));
      btn.classList.add('active');
      render(currentStory);
    });
    tabsEl.appendChild(btn);
  }
}

function formatBeat(beat: Beat): string {
  switch (beat.type) {
    case 'line': {
      const line = lineCatalog[beat.key];
      return line
        ? `<span class="b b-line"><b>${escape(line.voice)}:</b> ${escape(line.text)}</span>`
        : `<span class="b b-line">line ${escape(beat.key)} <i>(missing)</i></span>`;
    }
    case 'choice':
      return `<span class="b b-choice">choice → ${beat.options.map(o => escape(o.text)).join(' | ')}</span>`;
    case 'goto':
      return `<span class="b b-goto">→ ${escape(beat.scene)}</span>`;
    case 'end':
      return `<span class="b b-end">end (${escape(beat.ending)})</span>`;
    case 'set':       return `<span class="b">set: ${escape(beat.key)}</span>`;
    case 'place': {
      const cast = (beat.characters ?? []).map(c => typeof c === 'string' ? c : c.who).join(', ');
      const props = (beat.props ?? []).join(', ');
      const parts: string[] = [`set ${escape(beat.set)}`];
      if (props) parts.push(`props: ${escape(props)}`);
      if (cast)  parts.push(`cast: ${escape(cast)}`);
      if (beat.music) parts.push(`music: ${escape(beat.music)}`);
      return `<span class="b">place — ${parts.join(' · ')}</span>`;
    }
    case 'curtain':   return `<span class="b">curtain ${beat.action}</span>`;
    case 'music':     return `<span class="b">music: ${escape(beat.key ?? '(stop)')}</span>`;
    case 'enter':     return `<span class="b">enter ${escape(beat.who)} (${escape(beat.side ?? 'center')})</span>`;
    case 'exit':      return `<span class="b">exit ${escape(beat.who)} (${escape(beat.side ?? 'left')})</span>`;
    case 'sfx':       return `<span class="b">sfx: ${escape(beat.key)}</span>`;
    case 'wait':      return `<span class="b">wait ${beat.ms}ms</span>`;
    case 'view':      return `<span class="b">view: ${escape(beat.view)}</span>`;
    case 'ambient':   return `<span class="b">ambient: ${escape(beat.key ?? '(stop)')}</span>`;
    case 'puppet':    return `<span class="b">puppet ${beat.visible ? 'show' : 'hide'}</span>`;
    case 'prop':      return `<span class="b">prop ${escape(beat.key)} ${beat.action}</span>`;
    case 'move':      return `<span class="b">move ${escape(beat.who)} → x=${beat.x}</span>`;
    case 'lean':      return `<span class="b">lean ${escape(beat.who)} ${beat.deg}°</span>`;
    case 'shake':     return `<span class="b">shake ${escape(beat.who)}</span>`;
    case 'voice_prompt':
      return `<span class="b b-choice">voice_prompt → spoke: ${escape(beat.onSpeak)} | silent: ${escape(beat.onSilence)}</span>`;
    case 'conversation':
      return `<span class="b b-choice">conversation 🗣 → ${escape(beat.onExit)}` +
        (beat.passMemory ? ' <i>(passes memory)</i>' : '') +
        '</span>';
    case 'branch_if_spoke':
      return `<span class="b b-choice">branch_if_spoke → spoke: ${escape(beat.whenSpoken)} | silent: ${escape(beat.whenSilent)}</span>`;
    case 'set_flag':
      return `<span class="b">set_flag ${escape(beat.key)} = ${escape(String(beat.value ?? true))}</span>`;
    case 'branch_if_flag':
      return `<span class="b b-choice">branch_if_flag ${escape(beat.key)}` +
        (beat.equals !== undefined ? `=${escape(String(beat.equals))}` : '') +
        ` → match: ${escape(beat.whenMatch)} | else: ${escape(beat.whenNotMatch)}</span>`;
    case 'branch_if_count':
      return `<span class="b b-choice">branch_if_count [${beat.flags.map(escape).join(', ')}] ≥${beat.atLeast} → ${escape(beat.whenAtLeast)} | else: ${escape(beat.whenBelow)}</span>`;
    default:          return `<span class="b">${escape(JSON.stringify(beat))}</span>`;
  }
}

function escape(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

buildTabs();
setupPanZoom();
render(currentStory);
window.addEventListener('resize', () => {
  // Re-fit graph to viewport on resize.
  const main = document.querySelector('.story-main') as HTMLElement | null;
  const graph = document.getElementById('graph');
  if (!main || !graph) return;
  resetViewport(graph.offsetWidth, graph.offsetHeight);
});

document.getElementById('story-fit')?.addEventListener('click', () => {
  const graph = document.getElementById('graph');
  if (!graph) return;
  resetViewport(graph.offsetWidth, graph.offsetHeight);
});
